// ============================================================
// niNumber.ts — THE UK National Insurance number rule (pure; niNumber.test.ts).
// One rule — NORMALISE, then MATCH — used by the case/client form's validation
// (utils/validation.ts) and, mirrored byte-for-byte in
// backend/src/lib/niNumber.ts, by the AI briefing's redaction. A parity test
// (backend/test/audit-niNumber-parity.test.ts) keeps the two copies identical.
//   normalise: upper case; spaces and the separators people type (- . /) removed
//     "ab 12 34 56 c" / "AB-12-34-56-C" / "AB1 23 45 6C" → "AB123456C"
//   valid (the form): 2 letters + 6 digits + a suffix letter   AB123456C
//   looks like one (redaction): the same, suffix optional       AB123456
// Deliberately broader than the official prefix rules, so test numbers like
// QQ123456C are caught too.
// ============================================================

export const normaliseNiNumber = (raw: string): string => raw.toUpperCase().replace(/[\s.\-/]+/g, '')

const NI_VALID = /^[A-Z]{2}\d{6}[A-Z]$/
const NI_LIKE = /^[A-Z]{2}\d{6}[A-Z]?$/

// The form: is this a well-formed NI number (suffix required)?
export const isValidNiNumber = (raw: string): boolean => NI_VALID.test(normaliseNiNumber(raw))

// Redaction: is this NI-number-shaped (suffix optional)?
export const looksLikeNiNumber = (raw: string): boolean => NI_LIKE.test(normaliseNiNumber(raw))

// Free text: two letters, six digits (each optionally after a space / - . /),
// an optional suffix letter — every candidate is then normalised and matched
// with the rule above before it's replaced.
const NI_CANDIDATE = /\b[A-Z]{2}(?:[\s.\-/]*\d){6}(?:[\s.\-/]*[A-Z])?\b/gi
export const NI_REDACTED = '[NI number removed]'
export const redactNiNumbers = (text: string): string =>
  text.replace(NI_CANDIDATE, (m) => (looksLikeNiNumber(m) ? NI_REDACTED : m))
